<template>
    <div>
        <div v-if="measurementsExist">

            <MeasurementOptions />

            <div class="d-flex flex-wrap align-content-stretch">

              <!-- Metrics Tiles -->
              <div class="container">
                <b-row>
                  <b-card-group class="col-md-3 my-2 p-1" v-for="item in metricsItems" :key="item.type">
                    <MeasurementMetricTile :metricData="item"/>
                  </b-card-group>
                </b-row>
              </div>
              <!-- protocol summary -->
              <MeasurementProtocolSummary :session="session" :measurementData="measurementData"/>

              <!-- charts -->
              <MeasurementChart v-for="item in chartItems"
                                :key="item.type"
                                :chart-template="item"
                                :session="session"
                                :measurementData="measurementData"/>

            </div>
        </div>
        <div v-else class="text-center">
            <h5>No Measurement data available</h5>
        </div>
    </div>
</template>

<script>

    import MeasurementOptions from './MeasurementOptions'
    import MeasurementChart from './MeasurementChart'
    import MeasurementMetricTile from './MeasurementMetricTile'
    import MeasurementProtocolSummary from './MeasurementProtocolSummary'
    import { mapActions, mapGetters } from 'vuex'
    import measurementVisibility from "../../lib/MeasurementVisibility";
    import measurementTypes from "../../lib/MeasurementTypes";
    import measurementChannels from "../../lib/MeasurementChannel";

    export default {
        name: "MeasurementChartsTab",
        components: {
            MeasurementOptions,
            MeasurementChart,
            MeasurementMetricTile,
            MeasurementProtocolSummary
        },
        props: [],
        data() {
            return {
                chartItemsClass: 'w-50 p-2 text-center',
            }
        },
        created () {

        },
        computed: {
            ...mapGetters({

                session: 'session/current',
                sessionLoading: 'session/currentLoadingOrNull',

                measurementDataLoading: 'measurement/isLoading',
                measurementData: 'measurement/current',
                measurementSelectedMeasurements: 'measurement/selectedMeasurements',
            }),

            elapsedSeconds: function () {
                if (this.measurementData && this.measurementData.elapsedSeconds) {
                    return this.measurementData.elapsedSeconds;
                }

                return 0;
            },

            sessionInProgress() {
              return !this.sessionLoading && this.session && this.session.isComplete == false;
            },

            measurementsExist: function () {
                if (this.measurementsVisibleToUser.find(x => x.points && x.points.length > 0)) {
                    return true;
                } else {
                    return false;
                }
            },

            measurementsVisibleToUser: function() {
              if (!this.measurementData || !this.measurementData.data) {
                return [];
              }
              let self = this;
              return this.measurementData.data.filter(x => measurementVisibility.visibleToUser(x.measurementType, self.session)) ?? [];
            },

            metricsItems: function() {
              if(!this.measurementsExist || !this.measurementSelectedMeasurements) {
                return;
              }

              let selected = this.measurementSelectedMeasurements;
              let enabled = this.measurementsVisibleToUser.filter(function(item) {
                if(item.points && item.points.length > 0) {
                  return selected.find(x => x === item.measurementType) !== undefined;
                }
                return false;
              })

              let items = enabled.map(function(item) {
                let mapped = {
                  label: item.shortName,
                  subLabel: item.unitsName,
                  hasSubLabel: !!item.unitsName && item.unitsName.length > 0,
                  color: item.color
                };

                if(item.metrics) {
                  mapped.min = item.metrics.min;
                  mapped.max = item.metrics.max;
                  mapped.last = item.metrics.last;
                  mapped.average = item.metrics.average;
                } else {
                  mapped.min = 0;
                  mapped.max = 0;
                  mapped.last = 0;
                  mapped.average = 0;
                }
                return mapped;
              });

              return items;
            },

            chartItems: function() {

                if(!this.measurementsExist) {
                    return [];
                }

                let self = this;
                let selected = this.measurementSelectedMeasurements;
                let enabled = this.measurementsVisibleToUser.filter(function(item) {
                    return selected.find(x => x === item.measurementType) !== undefined;
                });

                let items = [];

                items = items.concat(self.createMetabolismChart(this.measurementData));
                items = items.concat(self.createVentilationChart(this.measurementData));
                items = items.concat(self.createGasExchangeChart(this.measurementData));
                items = items.concat(self.createMotionChart(this.measurementData));
                items = items.concat(self.createOxygenationPlot(this.measurementData));

                enabled.forEach(function(item) {
                  items.push(self.createChartTemplate(null, [item.id], true, false, false));
                });

                return items.filter(x => x !== undefined && x != null);
            }
        },
        methods: {
            ...mapActions({

            }),

            createChartTemplate: function(chartName, measurementIds, hideWhenEmpty, showTargetIntensity, showProtocolPhases) {

              return {

                id: '',

                chartName: chartName,

                measurementIds: measurementIds,

                hideWhenEmpty: hideWhenEmpty ?? true,
                showTargetIntensity: showTargetIntensity ?? true,
                showProtocolPhases: showProtocolPhases ?? true,
              };

            },

          createMetabolismChart(measurementData) {

            let measurements = [];
            let vo2Data = measurementData.data.find(x => measurementTypes.match(x.id , measurementTypes.OxygenConsumptionByWeight));
            if (!vo2Data) {
              measurements.push(measurementTypes.OxygenConsumption)
            } else {
              measurements.push(measurementTypes.OxygenConsumptionByWeight)
            }

            measurements.push(measurementTypes.HeartRate);

            return this.createChartTemplate('Metabolism', measurements)
          },

          createVentilationChart() {
            return this.createChartTemplate('Ventilation', [measurementTypes.RespiratoryFrequency, measurementTypes.TidalVolume, measurementTypes.Ventilation])
          },

          createGasExchangeChart(measurementData) {
            let eqO2 = measurementData.data.find(x => measurementTypes.match(x.id , measurementTypes.EqO2));
            if(eqO2) {
              return this.createChartTemplate('Gas Exchange', [measurementTypes.FractionOfExpiredOxygen, measurementTypes.EqO2])
            }

            return this.createChartTemplate('Gas Exchange', [measurementTypes.FractionOfExpiredOxygen, measurementTypes.VentilationVO2])
          },

          createMotionChart( measurementData ) {

            let result = [];

            let speedData = measurementData.data.find(x => x.id === measurementTypes.Speed);
            let cadenceData = measurementData.data.find(x => x.id === measurementTypes.Cadence);
            let powerDataCh1 = measurementData.data.find(x => x.id === measurementTypes.CyclingPower);
            let powerDataCh2 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.CyclingPower, measurementChannels.Second));
            let powerDataCh3 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.CyclingPower, measurementChannels.Third));

            //If all three power channels are used, create separate plots for motion and power
            if (powerDataCh1 && powerDataCh2 && powerDataCh3)
            {
              if (speedData || cadenceData)
              {
                result.push(this.createChartTemplate("Motion",[ cadenceData, speedData]));
              }

              result.push(this.createChartTemplate("Power",[ powerDataCh1, powerDataCh2, powerDataCh3]));
            }
            else if (speedData || cadenceData || powerDataCh1 || powerDataCh2 || powerDataCh3 )
            {
              result.push(this.createChartTemplate("Motion",[ cadenceData, speedData, powerDataCh1, powerDataCh2, powerDataCh3]));
            }

            return result;
          },

          createOxygenationPlot(measurementData) {
            let result = [];

            let smo2DataCh1 = measurementData.data.find(x => x.id === measurementTypes.SmO2);
            let thbDataCh1 = measurementData.data.find(x => x.id === measurementTypes.THb);
            let smo2DataCh2 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.SmO2, measurementChannels.Second));
            let thbDataCh2 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.THb, measurementChannels.Second));
            let smo2DataCh3 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.SmO2, measurementChannels.Third));
            let thbDataCh3 = measurementData.data.find(x => x.id === measurementTypes.createIdentifier( measurementTypes.THb, measurementChannels.Third));

            let smo2Data = [smo2DataCh1, smo2DataCh2, smo2DataCh3 ].filter(x => x !== undefined && x != null);
            let thbData = [thbDataCh1, thbDataCh2, thbDataCh3 ].filter(x => x !== undefined && x != null);

            //If there are three smo2 datasets or three thb datasets, break into two graphs
            if (smo2Data.length >= 3 || thbData.length >= 3)
            {
              result.push(this.createChartTemplate("SmO2", [ smo2DataCh1, smo2DataCh3, smo2DataCh2]));
              result.push(this.createChartTemplate( "tHb",[ thbDataCh1, thbDataCh3, thbDataCh2]));
            }
            else if (smo2Data.length > 0 || thbData.length > 0)
            {
              //Ordered to maximize shared axis
              result.push(this.createChartTemplate("Muscle-Oxygenation", [ smo2DataCh1, smo2DataCh3, thbDataCh1,smo2DataCh2, thbDataCh2, thbDataCh3]));
            }

            return result;
          }

        }
    }
</script>

<style scoped>

.fade-in-enter-active {
  transition: opacity 300ms cubic-bezier(0.55, 0.085, 0.68, 0.53);
}

.fade-in-leave-active {
  transition: opacity 225ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-in-enter,
.fade-in-leave-to {
  opacity: 0;
}

.metric-color-tile {
  width: 16px; height: 16px;
  border: 1px solid #17191B;
  vertical-align: middle;
  margin: 2px 6px;
}

</style>